import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { t } from '../../../i18n/i18n';
import './Payout.scss';

/**
 * Placeholders for the Payout page's sections while their own request is in
 * flight. The page draws from four endpoints that land at different times, so
 * each section waits on its own rather than the page waiting on all four:
 *
 *   /payouts/eligibility/  the hero
 *   /payouts/summary/      the four stat cards
 *   /payouts/journey/      the payout journey card
 *   /payouts/history/      the table, which already takes `loading`
 *
 * The lifetime payouts curve has none here: its card keeps its own chrome
 * while it reads, and `LineChart` draws the plot's placeholder itself.
 *
 * Each piece renders the page's own layout classes — `payout-hero`,
 * `journey-card`, `stat-card`, `payouts-card` — so the box is already the size
 * it will be. The hero alone is 260px; a page that paints the stat row first
 * and is then pushed down by it is a worse first paint than one that
 * never moves. Bar heights follow each line's own `line-height` for the same
 * reason.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

/**
 * The hero.
 *
 * Plain tone, not `light`: the hero's fill is the same near-black as the cards
 * below it. No "Payout blocked" badge — most accounts aren't, and a badge that
 * then vanishes would pull the rest of the column up with it.
 */
export function HeroSkeleton() {
  return (
    <section className="payout-hero" aria-busy="true" aria-label={t('Loading payout totals')}>
      <div className="payout-hero-content">
        {/* `payout-info` is only ever as wide as its text; with bars in it
            that is zero, so the skeleton claims the row for the % widths. */}
        <div className="payout-info payout-info--skel">
          <Skeleton w="168px" h={12} />
          <Skeleton w="min(280px, 70%)" h={49} />
          <Skeleton w="min(420px, 90%)" h={20} />
          <Skeleton w="min(260px, 70%)" h={15} />
        </div>

        <div className="payout-action">
          <Skeleton w="100%" h={40} radius={8} />
          <Skeleton w="80%" h={14} className="payout-wait--skel" />
        </div>
      </div>
    </section>
  );
}

/**
 * The journey card below its head — the five meta fields and the stage track.
 *
 * Its own: picking another reference re-reads only this much, and the tablist
 * that was just clicked has to stay on screen and stay clickable.
 */
export function JourneyBodySkeleton() {
  return (
    <div className="journey-body--skel" aria-busy="true" aria-label={t('Loading payout journey')}>
      <div className="journey-meta">
        {range(5).map((i) => (
          <div className="journey-meta-item" key={i}>
            <Skeleton w="min(72px, 80%)" h={12} />
            <Skeleton w="min(104px, 95%)" h={15} />
          </div>
        ))}
      </div>

      {/* The track's own grid, so the dots land where the real ones will. */}
      <div className="journey-track">
        <div className="journey-steps">
          {range(5).map((i) => (
            <div className="journey-step" key={i}>
              <Skeleton w="20px" h={20} pill />
              <Skeleton w="min(96px, 80%)" h={15} />
              <Skeleton w="min(64px, 60%)" h={12} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The whole journey card — while the in-flight list itself is reading.
 *
 * The reference tablist is a bar rather than live `SegmentedTabs`: there are
 * no references to switch between until that response names them.
 */
export function JourneySkeleton() {
  return (
    <section className="journey-card" aria-busy="true" aria-label={t('Loading payout journey')}>
      <div className="journey-head">
        <div className="journey-head-left">
          <Skeleton w="18px" h={18} pill />
          <Skeleton w="104px" h={15} />
        </div>
        <Skeleton w="150px" h={32} radius={8} />
      </div>

      <span className="journey-divider" />

      <JourneyBodySkeleton />

      {/* The note the real card always prints under the track. */}
      <Skeleton w="min(320px, 60%)" h={15} />
    </section>
  );
}

/** The four totals below the hero. */
export function StatsSkeleton() {
  return (
    <section className="stats-row" aria-busy="true" aria-label={t('Loading payout totals')}>
      {range(4).map((i) => (
        <div className="stat-card" key={i}>
          <Skeleton w="min(112px, 85%)" h={12} />
          <Skeleton w="min(92px, 70%)" h={20} />
        </div>
      ))}
    </section>
  );
}
